import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Newspaper component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconNewspaper extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.55273 3.50415C1.83973 3.21714 2.22899 3.05591 2.63488 3.05591H17.4622C17.8681 3.05591 18.2573 3.21714 18.5443 3.50415C18.8313 3.79115 18.9926 4.18041 18.9926 4.58629V18.243C18.9926 18.5615 19.1191 18.867 19.3443 19.0922C19.5695 19.3175 19.875 19.444 20.1935 19.444C20.512 19.444 20.8175 19.3175 21.0427 19.0922C21.268 18.867 21.3945 18.5615 21.3945 18.243V8.09802C21.3945 7.68381 21.7303 7.34802 22.1445 7.34802C22.5587 7.34802 22.8945 7.68381 22.8945 8.09802V18.243C22.8945 18.9594 22.6099 19.6464 22.1034 20.1529C21.5969 20.6594 20.9099 20.944 20.1935 20.944H3.41526C2.80241 20.944 2.21465 20.7005 1.7813 20.2672C1.34795 19.8338 1.10449 19.2461 1.10449 18.6332V4.58629C1.10449 4.18041 1.26573 3.79115 1.55273 3.50415ZM17.7743 19.444C17.5907 19.0742 17.4926 18.6637 17.4926 18.243V4.58629C17.4926 4.57823 17.4894 4.5705 17.4837 4.56481C17.478 4.55911 17.4702 4.55591 17.4622 4.55591H2.63488C2.62682 4.55591 2.61909 4.55911 2.61339 4.56481C2.60769 4.57051 2.60449 4.57823 2.60449 4.58629V18.6332C2.60449 18.8482 2.68991 19.0545 2.84196 19.2065C2.99401 19.3586 3.20023 19.444 3.41526 19.444H17.7743ZM5.78687 7.70776C5.78687 7.29355 6.12265 6.95776 6.53687 6.95776H13.5603C13.9745 6.95776 14.3103 7.29355 14.3103 7.70776V11.6097C14.3103 12.0239 13.9745 12.3597 13.5603 12.3597H6.53687C6.12265 12.3597 5.78687 12.0239 5.78687 11.6097V7.70776ZM7.28687 8.45776V10.8597H12.8103V8.45776H7.28687ZM6.53687 15.5422C6.12265 15.5422 5.78687 15.878 5.78687 16.2922C5.78687 16.7064 6.12265 17.0422 6.53687 17.0422H13.5603C13.9745 17.0422 14.3103 16.7064 14.3103 16.2922C14.3103 15.878 13.9745 15.5422 13.5603 15.5422H6.53687Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-newspaper') === undefined) {
  customElements.define('cat-icon-newspaper', BlocksIconNewspaper);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-newspaper': BlocksIconNewspaper;
  }
}
